import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { NavigateFunction } from 'react-router-dom';

export interface PushInitOptions {
  onToken: (token: string, platform: string) => void;
  navigate: NavigateFunction;
}

/**
 * Registers for native push notifications (FCM on Android, APNs on iOS).
 * No-ops on web/Electron. Must be called after the user is authenticated.
 *
 * Prerequisites before this will work:
 *   - Android: google-services.json in android/app/
 *   - iOS: GoogleService-Info.plist in ios/App/App/ + Push Notifications capability in Xcode
 */
export async function initPushNotifications({ onToken, navigate }: PushInitOptions): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // Create a high-importance channel before requesting permission so Android
  // uses it for all subsequent notifications (sound + heads-up popup).
  if (Capacitor.getPlatform() === 'android') {
    await PushNotifications.createChannel({
      id: 'order-alerts',
      name: 'Order Alerts',
      description: 'New order notifications',
      importance: 5,        // IMPORTANCE_HIGH — heads-up popup + sound
      sound: 'money_notification',
      vibration: true,
      visibility: 1,        // VISIBILITY_PUBLIC
    });
  }

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== 'granted') return;

  // Wrap registration in try/catch — Firebase crashes the app if google-services.json
  // (Android) or GoogleService-Info.plist (iOS) is not yet configured.
  try {
    await PushNotifications.register();
  } catch (err) {
    console.warn('[push] Registration skipped — Firebase not configured yet:', err);
    return;
  }

  PushNotifications.addListener('registration', (token) => {
    onToken(token.value, Capacitor.getPlatform());
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('[push] registration failed:', err.error);
  });

  // App is in foreground — SSE already handles this event; we receive for deduplication only
  PushNotifications.addListener('pushNotificationReceived', (_notification) => {
    // Dedupe by notification.data.eventId vs SSE-delivered events if needed
  });

  // User tapped a notification while app was backgrounded or closed
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const { orderId, restaurantId } = (action.notification.data ?? {}) as {
      orderId?: string;
      restaurantId?: string;
    };
    if (restaurantId && orderId) {
      navigate(`/app/restaurants/${restaurantId}/orders`);
    } else if (restaurantId) {
      navigate(`/app/restaurants/${restaurantId}`);
    }
  });
}

export async function teardownPushNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  await PushNotifications.removeAllListeners();
}
